// src/screens/GestionReportesScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { IoArrowBack, IoEyeOutline, IoDocumentAttachOutline, IoWarningOutline } from 'react-icons/io5';
import moment from 'moment';
import 'moment/locale/es';

import './GestionReportesScreen.css';

import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoBottomNav from '../components/Shared/KondoBottomNav';

// --- Components ---
const EstatusReporte = ({ estatus }) => {
  let className = 'status-pill';
  // Normalize status text just in case
  const s = estatus || 'Pendiente';
  if (s === 'Pendiente') className += ' pendiente';
  else if (s === 'En Proceso') className += ' en-proceso';
  else if (s === 'Solucionado') className += ' solucionado';
  else if (s === 'Cancelado') className += ' cancelado';

  return <span className={className}>{s}</span>;
};

const ReporteAdjunto = ({ url }) => {
  if (!url) return <span style={{ color: '#d1d5db' }}>-</span>;

  if (/\.(jpeg|jpg|gif|png|webp)$/i.test(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        <img src={url} alt="Adjunto" className="report-thumbnail" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#6b7280', fontSize: '24px', display: 'flex', justifyContent: 'center' }}>
      <IoDocumentAttachOutline />
    </a>
  );
};

function GestionReportesScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!userData?.condominioId) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "reportes"),
      where("condominioId", "==", userData.condominioId),
      orderBy("estatus", "asc"),
      orderBy("fechaCreacion", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const listaReportes = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReportes(listaReportes);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar reportes: ", error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [userData]);

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/gestion-reportes" />
      <main className="ks-main">
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
          <div className="reporte-dashboard-container">
            {/* --- Premium Banner --- */}
            <div className="muro-header-stitch">
              <div className="muro-hero-card">
                <div className="muro-hero-content">
                  <div className="muro-hero-text">
                    <h1>Bandeja de Reportes</h1>
                    <p>Gestiona las incidencias de los residentes</p>
                  </div>
                </div>
                <IoWarningOutline className="muro-hero-icon-decor" />
              </div>
            </div>

            {/* --- Main Card --- */}
            <div className="reporte-card">
              {loading && (
                <div className="empty-state">
                  <p>Cargando reportes...</p>
                </div>
              )}

              {!loading && reportes.length === 0 && (
                <div className="empty-state">
                  <IoWarningOutline size={48} style={{ opacity: 0.3, marginBottom: 10 }} />
                  <p>No hay reportes registrados.</p>
                </div>
              )}

              {!loading && reportes.length > 0 && (
                isMobile ? (
                  <div className="mobile-reports-list">
                    {reportes.map((reporte) => (
                      <div key={reporte.id} className="mobile-report-card">
                        <div className="mrc-header">
                          <span className="mrc-tipo">{reporte.tipo}</span>
                          <span className="mrc-date">{reporte.fechaCreacion ? moment(reporte.fechaCreacion.toDate()).format('D MMM, HH:mm') : 'Sin fecha'}</span>
                        </div>
                        <div className="mrc-body">
                          <div className="mrc-resident">{reporte.nombreResidente || 'Anónimo'}</div>
                          <div className="mrc-unit">{reporte.unidadResidente || 'N/A'}</div>
                          <div className="mrc-desc">{reporte.descripcion}</div>
                        </div>
                        <div className="mrc-footer">
                          <EstatusReporte estatus={reporte.estatus} />
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            {reporte.archivoUrl && <ReporteAdjunto url={reporte.archivoUrl} />}
                            <button
                              className="action-btn-primary"
                              onClick={() => navigate(`/admin-reporte/${reporte.id}`)}
                            >
                              <IoEyeOutline size={18} /><span style={{ marginLeft: '6px' }}>Atender</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="modern-table">
                      <colgroup>
                        <col className="col-estatus" />
                        <col />
                        <col />
                        <col className="col-tipo" />
                        <col />
                        <col />
                        <col className="col-accion" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th className="col-estatus">Estatus</th>
                          <th style={{ textAlign: 'center' }}>Adjunto</th>
                          <th>Residente / Unidad</th>
                          <th className="col-tipo">Tipo</th>
                          <th>Descripción</th>
                          <th>Fecha</th>
                          <th className="col-accion" style={{ textAlign: 'right' }}>Acción</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportes.map((reporte) => (
                          <tr key={reporte.id}>
                            <td className="col-estatus"><EstatusReporte estatus={reporte.estatus} /></td>
                            <td style={{ textAlign: 'center' }}>
                              <ReporteAdjunto url={reporte.archivoUrl} />
                            </td>
                            <td>
                              <div style={{ fontWeight: 600, color: '#1f2937' }}>{reporte.nombreResidente || 'Anónimo'}</div>
                              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{reporte.unidadResidente || 'N/A'}</div>
                            </td>
                            <td className="col-tipo">
                              <span className="tipo-badge">
                                {reporte.tipo}
                              </span>
                            </td>
                            <td className="col-descripcion" title={reporte.descripcion}>
                              {reporte.descripcion}
                            </td>
                            <td style={{ minWidth: '140px', fontSize: '0.9rem' }}>
                              {reporte.fechaCreacion ? moment(reporte.fechaCreacion.toDate()).format('D MMM, HH:mm') : 'Sin fecha'}
                            </td>
                            <td className="col-accion" style={{ textAlign: 'right' }}>
                              <button
                                className="action-btn-primary"
                                style={{ marginLeft: 'auto' }}
                                onClick={() => navigate(`/admin-reporte/${reporte.id}`)}
                              >
                                <IoEyeOutline size={18} /><span style={{ marginLeft: '6px' }}>Atender</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </main>
      <KondoBottomNav activeRoute="/gestion-reportes" />
    </div>
  );
}

export default GestionReportesScreen;